import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import PropTypes from 'prop-types';

import { downloadFile } from "reducers/Download";
import { confirmDeleteLibrary } from "reducers/Confirm";
import { deleteLibrary } from "reducers/LibraryProperties";

import { hasPermission } from "utils/common";
import { API_URL } from "utils/constants";
import Permissioned from "widgets/Permissioned";
import ActionMenu, { LinkAction, BasicAction } from "widgets/ActionMenu";
import { useClearLibraryFilters } from "hooks/useClearFilters";


let deleteLibraryAction = {
	shouldDisplay: ({ user }) => {
		return hasPermission(user, "Creative Library", "Delete");
	},
	component: ({ libraryId, dependentValues, onLibraryDeleteSuccess }) => {
		let dispatch = useDispatch();
		return (
			<BasicAction
				name="Delete Creative Library"
				iconName="delete"
				handleClick={() => {
					dispatch(confirmDeleteLibrary(deleteLibrary, { id:libraryId, name:dependentValues.name }, onLibraryDeleteSuccess));
				}}
			/>
		);
	},
};

let downloadLibraryAction = {
	shouldDisplay: ({ user }) => {
		return hasPermission(user, "Creative Library", "View");
	},
	component: ({ libraryId }) => {
		let dispatch = useDispatch();
		return (
			<BasicAction
				name = "Download Summary"
				iconName = "download"
				handleClick = {() => {
					dispatch(downloadFile(`${API_URL}/crm/v1/creative-libraries/${libraryId}/summary`));
				}}
			/>
		);
	},
};

const commonLinkActionProps={
	useMenuItem:false,
	className:"action-link",
	iconClass:"bounce-circle",
};

export function LibraryActions(props){
	let { value: libraryId, dependentValues } = props
		, user          = useSelector(state => state.you)
		, actionLists   = [[downloadLibraryAction], [deleteLibraryAction]]
		, actionContext = {
			user,
			libraryId,
			dependentValues,
		}
		;

	const clearFilters = useClearLibraryFilters();

	return (
		<div className="grid-actions flex-center">
			<LinkAction
				{...commonLinkActionProps}
				path={`/library/${libraryId}/`}
				iconName="far fa-folder-open"
				key="Open Library"
				className="library-action"
				tooltipContent="Open Library"
				onClick={clearFilters}
			/>
			<Permissioned
				domain="Creative Library"
				action="Edit">
				<LinkAction
					{...commonLinkActionProps}
					path={`/libraries/modal/edit/${libraryId}/`}
					iconName="far fa-cog"
					tooltipContent={"Edit Library Settings"}
				/>
			</Permissioned>
			<Permissioned
				domain="Creative Library"
				action="Share Creative Library">
				<LinkAction
					{...commonLinkActionProps}
					path={`/libraries/modal/share/${libraryId}/`}
					iconName="far fa-share"
					tooltipContent="Share Library"
						 />
			</Permissioned>
			<ActionMenu
				actionLists = {actionLists}
				actionContext = {actionContext}
				tooltipContent = "More Actions"
				menuClass = "cypress-creative-library-actions-more"
			/>
		</div>
	);
}

function getActionMetaData(row){
	return {
		name:row.name,
	};
}

const LibraryActionsCell = {
	key: "actions",
	name: "Actions",
	cellClass: "actions-cell cypress-creative-library-actions",
	width:130,
	formatter: LibraryActions,
	resizable:false,
	sortable:false,
	getRowMetaData: getActionMetaData,
};


export function LibraryActionList({
	libraryId,
	libraryName,
	className,
	onLibraryDeleteSuccess,
}){
	const currentUser = useSelector(state => state.you);
	const actionLists = [[{
		shouldDisplay:({ user }) => {
			return hasPermission(user, "Creative Library", "Edit");
		},
		component: ({ libraryId }) => {
			let dispatch = useDispatch();
			return (
				<BasicAction
					name = "Library Settings"
					iconName = "fas fa-cog"
					handleClick = {() => {
						dispatch(push(`/library/${libraryId}/modal/edit/`));
					}}
				/>
			);
		},
	}, {
		shouldDisplay:({ user }) => {
			return hasPermission(user, "Creative Library", "Share Creative Library");
		},
		component: ({ libraryId }) => {
			let dispatch = useDispatch();
			return (
				<BasicAction
					name = "Share Library"
					iconName = "fas fa-share"
					handleClick = {() => dispatch(push(`/library/${libraryId}/modal/share`))}
				/>
			);
		},
	}, downloadLibraryAction],
	[deleteLibraryAction]];

	const actionContext = {
		libraryId,
		onLibraryDeleteSuccess,
		user: currentUser,
		dependentValues: {
			name: libraryName,
		},
	};

	return (
		<ActionMenu
			className={className}
			actionLists={actionLists}
			actionContext={actionContext}
		/>
	);
}

LibraryActionList.propTypes = {
	libraryId: PropTypes.string.isRequired,
	onLibraryDeleteSuccess: PropTypes.func.isRequired,
	libraryName: PropTypes.string,
	className: PropTypes.string,
};

LibraryActionList.defaultProps = { className: '' };

export default LibraryActionsCell;
